import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import type { PushDevice } from '@nucleus-commerce/api-client/admin';

import { pushApi } from '../api/push-api';
import {
  currentSubscription,
  notificationPermission,
  pushSupport,
  subscribe,
  subscribedWith,
  toDeviceBody,
} from '../lib/push-browser';

import { pushKeys } from './push-keys';

/**
 * This browser's device row, or null when it is not subscribed. Re-registers the subscription it
 * finds (fresh keys, a row the server dropped) and moves it to a rotated server key without
 * asking again, since permission is already granted.
 */
export async function syncThisDevice(publicKey: string): Promise<PushDevice | null> {
  if (pushSupport() !== 'supported' || notificationPermission() !== 'granted') return null;
  const found = await currentSubscription();
  if (!found) return null;
  const subscription = subscribedWith(found, publicKey) ? found : await subscribe(publicKey);
  return pushApi.register(toDeviceBody(subscription));
}

export function thisDeviceQueryOptions(publicKey: string) {
  return queryOptions({
    queryKey: pushKeys.thisDevice(publicKey),
    queryFn: () => syncThisDevice(publicKey),
    staleTime: Infinity,
    retry: false,
  });
}

/** The sync may have created or moved the row, so the device list is refetched once it lands. */
export function useThisDevice(publicKey: string | undefined) {
  const queryClient = useQueryClient();
  const query = useQuery({
    ...thisDeviceQueryOptions(publicKey ?? ''),
    enabled: Boolean(publicKey),
  });
  const deviceId = query.data?.id;
  useEffect(() => {
    if (deviceId) void queryClient.invalidateQueries({ queryKey: pushKeys.settings() });
  }, [deviceId, queryClient]);
  return query;
}
